import { ParadataEventEnum, ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { SurveyCard } from '@/surveys/states/surveys/survey-card';
import { Component, HostListener, Inject, OnDestroy, OnInit, signal, computed, inject, DestroyRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { FileSaverService } from 'ngx-filesaver';
import { Subscription, lastValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilingStatusDialogData } from '../../dialogs-data/filing-status-dialog-data';
import { FilingStatusDialogHttpService } from '../../services/filing-status-dialog-http.service';
import { CommonModule, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LinearLoadingComponent } from '@/common/components/linear-loading/linear-loading.component';

@Component({
    selector: 'app-filing-status-dialog',
    templateUrl: './filing-status-dialog.component.html',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatPaginatorModule,
        MatTableModule,
        MatTooltipModule,
        LinearLoadingComponent
    ],
    providers: [
        ParadataEventService,
        FilingStatusDialogHttpService,
        FileSaverService,
        DatePipe
    ]
})
export class FilingStatusDialogComponent implements OnInit, OnDestroy {
    private destroyRef = inject(DestroyRef);
    private paradataEventService = inject(ParadataEventService);
    private filingStatusDialogHttpService = inject(FilingStatusDialogHttpService);
    private fileSaverService = inject(FileSaverService);
    private matDialogRef = inject(MatDialogRef<FilingStatusDialogComponent>);

    public filingStatusDialogData = inject<FilingStatusDialogData>(MAT_DIALOG_DATA);

    // Signals for reactive state
    private filingStatusError = signal<string>('');
    private filingStatusButtonDisabled = signal<boolean>(false);
    private survey = signal<SurveyCard | null>(null);
    forms = signal<any[]>([]);
    private totalElements = signal<number>(0);
    private totalPages = signal<number>(0);
    private errorMessage = signal<string>('');
    private sendingRequest = signal<boolean>(false);
    private totalReceived = signal<any>(null);
    private order = signal<string>('');
    private reverse = signal<boolean>(false);
    exportOps = signal<boolean>(false);
    private onexport = signal<boolean>(false);
    private onul = signal<boolean>(false);
    private exporting = signal<boolean>(false);
    private pageIndex = signal<number>(0);
    private pageSize = signal<number>(10);

    // Computed properties
    currentFilingStatusError = computed(() => this.filingStatusError());
    isFilingStatusButtonDisabled = computed(() => this.filingStatusButtonDisabled());
    currentSurvey = computed(() => this.survey());
    currentForms = computed(() => this.forms());
    currentTotalElements = computed(() => this.totalElements());
    currentTotalPages = computed(() => this.totalPages());
    currentErrorMessage = computed(() => this.errorMessage());
    isSendingRequest = computed(() => this.sendingRequest());
    currentTotalReceived = computed(() => this.totalReceived());
    currentOrder = computed(() => this.order());
    isReverse = computed(() => this.reverse());
    isExportOps = computed(() => this.exportOps());
    isOnexport = computed(() => this.onexport());
    isOnul = computed(() => this.onul());
    isExporting = computed(() => this.exporting());
    currentPageIndex = computed(() => this.pageIndex());
    currentPageSize = computed(() => this.pageSize());
    // "Viewing 21-25 of 25": position of the first and last row shown on this page
    viewingStart = computed(() => this.currentPageIndex() * this.currentPageSize() + 1);
    viewingEnd = computed(() => this.currentPageIndex() * this.currentPageSize() + this.currentForms().length);

    // Default sorting parameters
    sortParameters = {
        direction: 'asc',
        property: 'organizationContactInfo.organization.brOrganizationId',
        size: 10,
        page: 0
    };

    constructor() { }

    ngOnInit() {
        // Add null check for filingStatusDialogData
        if (!this.filingStatusDialogData) {
            console.error('FilingStatusDialogData is undefined');
            return;
        }

        // Add null check for survey
        if (!this.filingStatusDialogData.survey) {
            console.error('Survey data is undefined in FilingStatusDialogData');
            return;
        }

        this.survey.set(this.filingStatusDialogData.survey);
        this.getData(0);
        this.getTotalReceived();
    }

    pageCount() {
        return Math.max(1, Math.ceil(this.currentTotalElements() / this.currentPageSize()));
    }

    hasMoreOnLeft() {
        return this.currentPageIndex() > 0;
    }

    hasMoreOnRight() {
        return this.currentPageIndex() < this.pageCount() - 1;
    }

    getPagingList() {
        return Array.from({ length: this.pageCount() }, (_, i) => i + 1);
    }

    goToPage(index: number) {
        if (index < 0 || index >= this.pageCount() || index === this.currentPageIndex()) {
            return;
        }
        this.pageIndex.set(index);
        this.getData(index);
    }

    pageItem(item: number) {
        this.goToPage(item - 1);
    }

    pageChange(direction: string) {
        this.goToPage(this.currentPageIndex() + (direction === 'left' ? -1 : 1));
    }

    // Close dialog
    closeDialog() {
        this.cancel();
    }

    onExport() {
        this.onexport.set(true);
        this.updateExport();
    }

    offExport() {
        this.onexport.set(false);
        this.updateExport();
    }

    onUl() {
        this.onul.set(true);
        this.updateExport();
    }

    offUl() {
        this.onul.set(false);
        this.updateExport();
    }

    updateExport() {
        if (this.isOnexport() || this.isOnul()) {
            this.exportOps.set(true);
        } else {
            setTimeout(() => {
                if (!(this.isOnexport() || this.isOnul())) {
                    this.exportOps.set(false);
                }
            }, 200);
        }
    }

    fasClass(column: string) {
        if (this.currentOrder() === column) {
            if (this.isReverse()) {
                return 'fa-sort-down';
            } else {
                return 'fa-sort-up';
            }
        } else {
            return 'fa-sort';
        }
    }

    ariaSort(column: string) {
        if (this.currentOrder() === column) {
            if (this.isReverse()) {
                return 'descending';
            } else {
                return 'ascending';
            }
        } else {
            return 'none';
        }
    }

    // Check to see if user is using Safari (it doesn't support exorting)
    safariCheck() {
        /*
        if (navigator.userAgent.toLowerCase().includes('safari')) {
            return true;
        }
        */
        return false;
    }

    page(pageEvent: PageEvent) {
        this.getData(pageEvent.pageIndex);
    }

    reorder(attrib: string) {
        if (this.currentOrder() !== attrib) {
            this.reverse.set(false);
            this.order.set(attrib);
        } else {
            this.reverse.set(!this.isReverse());
        }

        // City and State live inside organizationAddresses[0]
        const valueOf = (row: any) =>
            attrib === 'city' || attrib === 'state' ? row.organizationAddresses?.[0]?.[attrib] : row[attrib];
        const reverse = this.isReverse();

        // Sort a copy and set it, so the signal notifies the template
        const sorted = [...this.forms()].sort((a, z) => {
            const valueA = valueOf(a);
            const valueZ = valueOf(z);
            if (valueA == null && valueZ == null) { return 0; }
            if (valueA == null) { return reverse ? 1 : -1; }
            if (valueZ == null) { return reverse ? -1 : 1; }
            // numeric: true sorts "S99" before "S100"
            const result = String(valueA).localeCompare(String(valueZ), 'en', { sensitivity: 'base', numeric: true });
            return reverse ? -result : result;
        });
        this.forms.set(sorted);
    }

    // Change sorting parameters
    sortColumns(property: string) {
        if (property === this.sortParameters.property) {
            this.sortParameters.direction = this.sortParameters.direction === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortParameters.property = property;
            this.sortParameters.direction = 'asc';
        }

        this.getData(null);
    }

    // Load paginated data
    async getData(page: number) {
        this.paradataEventService.add(ParadataEventEnum.InvalidAuthenticationCode);
        if (page !== null && page !== undefined) {
            this.sortParameters.page = page;
        }
        this.errorMessage.set('');
        this.sendingRequest.set(true);
        // Keep the current rows on screen until the new page arrives. Clearing them here
        // made the dialog collapse and re-grow on every page change (looked like a refresh).
        const currentSurvey = this.currentSurvey();
        if (currentSurvey) {
            await lastValueFrom(this.filingStatusDialogHttpService.findAllSurveyForms(currentSurvey.surveyId, currentSurvey.surveyInstanceId, this.sortParameters)).then(
                (response: any) => {
                    this.forms.set(response.content);
                    this.totalElements.set(response.totalElements);
                    this.totalPages.set(response.totalPages);
                    this.sendingRequest.set(false);
                },
                (error: any) => {
                    this.forms.set([]);
                    this.sendingRequest.set(false);
                }
            );
        }
    }

    // Get the number of received results from the full set of forms
    getTotalReceived() {
        const currentSurvey = this.currentSurvey();
        if (currentSurvey) {
            this.filingStatusDialogHttpService.getTotalReceived(currentSurvey.surveyInstanceId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (response: any) => {
                        this.totalReceived.set(response);
                    },
                    error: (error: any) => {
                        this.sendingRequest.set(false);
                    }
                });
        }
    }

    exportStatus(format) {
        this.exporting.set(true);
        const currentSurvey = this.currentSurvey();
        if (currentSurvey) {
            this.filingStatusDialogHttpService.getFilingStatusReport(currentSurvey, format, this.sortParameters)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (response: any) => {
                        if (format === 'xls') {
                            this.paradataEventService.add(ParadataEventEnum.ExportExcel, currentSurvey.surveyId);
                            const xlsBlob = new Blob([response], { type: 'application/vnd.ms-excel' });
                            this.fileSaverService.save(xlsBlob, 'filing_status.xls');
                        } else if (format === 'csv') { // Default to xls
                            this.paradataEventService.add(ParadataEventEnum.ExportCSV, currentSurvey.surveyId);
                            const csvBlob = new Blob([response], { type: 'text/csv' });
                            this.fileSaverService.save(csvBlob, 'filing_status.csv');
                        } else {
                            this.paradataEventService.add(ParadataEventEnum.ExportExcel, currentSurvey.surveyId);
                            const csvBlob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                            this.fileSaverService.save(csvBlob, 'filing_status.xlsx');
                        }
                        this.exporting.set(false);
                    },
                    error: (error: any) => {
                        this.sendingRequest.set(false);
                        this.exporting.set(false);
                    }
                });
        }
    }

    ngOnDestroy() {
        this.paradataEventService.flushParadataevents();
    }

    cancel() {
        this.close('cancel');
    }

    close(value: any) {
        this.matDialogRef.close(value);
    }

    @HostListener('keydown.esc')
    public onEsc() {
        this.close('ESC keydown');
    }
}